import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import currency from "currency.js";
import SearchIcon from "@mui/icons-material/Search";
import { UserContext } from "../../context/UserContext";
import BDatePicker from "../../common/input/BDatePicker";
import { useLocation } from "react-router-dom";
import { capitalize, getQueryParam, getStoreFilterStreets, orderMatchesStoreFilter } from "../../common/util";
import { useNavigate } from "react-router";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link as RouterLink } from "react-router-dom";
import { STORAGE_BUCKET } from "../../common/constants";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ItemFilter from "../../planner/component/ItemFilter";

const columns = [
  {
    field: "timestamp",
    headerName: "Timestamp",
    type: "number",
    renderCell: (params) => (
      <div>
        <Box py={1}>
          <Typography variant="caption" display="block">
            {moment(params.value).format("ll")}
          </Typography>
          <Typography variant="overline" display="block" color="textSecondary">
            {moment(params.value).format("LT")}
          </Typography>
        </Box>
      </div>
    ),
    width: 100,
  },
  {
    field: "refund",
    headerName: "Amount",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 80,
  },
  {
    field: "uid",
    headerName: "By",
    renderCell: (params) => (
      <Avatar
        src={`${STORAGE_BUCKET}/img/u/${params.value}.webp`}
        sx={{ width: "32px", height: "32px" }}
      ></Avatar>
    ),
    width: 50,
  },
  {
    field: "reason",
    headerName: "Reason",
    width: 240,
    renderCell: (params) => (
      <div>
        <Typography variant="body2" display="block">
          {params.value}
        </Typography>
      </div>
    ),
  },
  {
    field: "name",
    headerName: "Customer",
    width: 160,
  },
  {
    field: "orderId",
    headerName: "Order",
    renderCell: (params) => (
      <IconButton component={RouterLink} to={`/order/${params.value}`}>
        <VisibilityOutlinedIcon />
      </IconButton>
    ),
    width: 50,
  },
];

export default function RefundsReport(props) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  let location = useLocation();

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");

  const [refunds, setRefunds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [storeFilter, setStoreFilter] = useState({});
  const apiRef = useRef(null);
  const storeLocations = user?.config?.storeLocation || [];
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  const visibleRefunds = refunds.filter((o) =>
    orderMatchesStoreFilter(
      { fulfillment: { storeLocationId: o.storeLocationId } },
      storeFilter?.store?.value,
      storeLocations
    )
  );
  const total = visibleRefunds.reduce((acc, o) => acc + +o.amount, 0);

  // Check params to identify search criteria
  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(location.search, "from"),
      getQueryParam(location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      // reset dates
      qFrom = moment().subtract(7, "days").format("YYYY-MM-DD");
      qTo = moment().format("YYYY-MM-DD");
    }
    search(qFrom, qTo);
  }, [location.search]);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    }
  }, [fromDt]);

  const runDateSearch = (e) => {
    e.preventDefault();
    navigate(`/report/refunds?from=${fromDt}&to=${toDt}`);
  };

  const search = (fromDate, toDate) => {
    setLoading(true);
    bkstApi.get(`/my/report/refundsv2?fromDt=${fromDate}&toDt=${toDate}`).then((res) => {
      let arr = res.data.refunds || [];

      setFromDt(fromDate);
      setToDt(toDate);

      arr.sort((o1, o2) => o1.ts - o2.ts);
      setRefunds(arr);

      setLoading(false);
    });
  };

  const exportToCSV = () => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv({
        fileName: `Refunds_${fromDt}_${toDt}`,
        fields: ["timestamp", "refund", "reason", "name"],
      });
    }
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Box>
        <Container maxWidth="md">
          <Box>
            <Box mt={4}>
              <Typography variant="h1" gutterBottom>
                Refunds Report
              </Typography>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                Refunds issued over a given time period
              </Typography>
              <Box my={3}>
                <form onSubmit={runDateSearch}>
                  <Box sx={{ display: "flex", gap: "10px" }}>
                    <BDatePicker
                      value={fromDt}
                      variant="filled"
                      label="From Date"
                      onChange={(d) => setFromDt(d)}
                      required
                      autoOk
                      disableFuture
                      hideIcon={true}
                    />
                    <BDatePicker
                      value={toDt}
                      variant="filled"
                      label="To Date"
                      onChange={(d) => setToDt(d)}
                      required
                      autoOk
                      disableFuture
                      hideIcon={true}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      style={{ height: "56px" }}
                    >
                      <SearchIcon />
                    </Button>
                  </Box>
                </form>
              </Box>
              {showStoreFilter && refunds.length > 0 && (
                <Box my={2}>
                  <ItemFilter
                    config={[{ name: "store", options: storeStreets }]}
                    data={refunds}
                    onChange={(o) => setStoreFilter({ ...storeFilter, ...o })}
                  />
                </Box>
              )}
            </Box>
          </Box>
          {visibleRefunds && visibleRefunds.length > 0 && (
            <Box my={2}>
              <Grid container spacing={2}>
                <Grid item xs={6} align="center">
                  <Box px={2} py={3} style={{ backgroundColor: "#ECECEC" }}>
                    <Typography variant="h4">{currency(total).format()}</Typography>
                    <Typography variant="overline" color="textSecondary">
                      TOTAL
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} align="center">
                  <Box px={2} py={3} style={{ backgroundColor: "#ECECEC" }}>
                    <Typography variant="h4">{visibleRefunds.length}</Typography>
                    <Typography variant="overline" color="textSecondary">
                      REFUNDS
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
          {visibleRefunds.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <IconButton onClick={exportToCSV}>
                <FileDownloadOutlinedIcon />
              </IconButton>
            </Box>
          )}
          <Box sx={{ width: "100%", display: "grid" }}>
            <DataGrid
              apiRef={apiRef}
              rows={visibleRefunds.map((o, idx) => {
                return {
                  id: idx,
                  orderId: o.id,
                  timestamp: o.ts,
                  name: o.customer,
                  refund: o.amount,
                  reason: `${capitalize(o.reason)}. ${o.note}`,
                  uid: o.uid,
                  type: o.type,
                };
              })}
              columns={columns}
              autoHeight
              getRowHeight={(params) => "auto"}
              disableRowSelectionOnClick
              initialState={{
                sorting: {
                  sortModel: [{ field: "time", sort: "desc" }],
                },
              }}
            />
          </Box>
        </Container>
      </Box>
    );
  }
}
